// @flow
import mergeWith from 'lodash/fp/mergeWith';
import fromEntries from 'lodash/fp/fromPairs';
import equals from 'lodash/fp/equals';
import compact from 'lodash/fp/compact';
import reverse from 'lodash/fp/reverse';
import flatten from 'lodash/fp/flatten';
import invariant from 'invariant';
import uniqBy from 'lodash/uniqBy';
import groupBy from 'lodash/fp/groupBy';

import type { Facet } from 'modules/facet';
import { byName as facetByName, termCountByPath } from 'modules/facet';
import { withoutProperty } from 'modules/object';
import { rootOutPaths as treeRootOutPaths } from 'modules/tree';

// eslint-disable-next-line no-use-before-define
export type TagTree = { +[key: string]: TagValue };

export type TagValue = $ReadOnlyArray<string> | TagTree;

export type ListOfTagTrees = $ReadOnlyArray<TagTree>;

export type Path = $ReadOnlyArray<string>;

export const mergedTagTrees = (
  treeA: TagTree = { ...null },
  treeB: TagTree = { ...null },
): TagTree =>
  mergeWith((a, b): TagTree[] | void =>
    [a, b].every((list) => Array.isArray(list)) ? [...a, ...b] : undefined,
  )(treeA, treeB);

export const mergedTagTreesWithoutDuplicates = (
  treeA: TagTree = { ...null },
  treeB: TagTree = { ...null },
): TagTree =>
  mergeWith((a, b): TagTree[] | void => {
    if (!a || !b) return undefined;

    if ([a, b].every((list) => Array.isArray(list))) {
      return uniqBy([...a, ...b]);
    }

    if (Array.isArray(a)) {
      return [...a, b];
    }

    if (Array.isArray(b)) {
      return [...b, a];
    }

    return undefined;
  })(treeA, treeB);

export const pathAsTagValue = ([head, ...rest]: Path): TagValue =>
  rest.length === 0 ? [head] : { [head]: pathAsTagValue(rest) };

export const pathAsTagTree = (
  path: Path,
  topLevelTagCollection?: string = 'General',
): TagTree => {
  const tagValue = pathAsTagValue(path);
  return Array.isArray(tagValue)
    ? { [topLevelTagCollection]: tagValue }
    : tagValue;
};

export const withPath: (
  topLevelCollection?: string,
) => (Path) => (TagTree) => TagTree =
  (topLevelTagCollection = 'General') =>
  (path) =>
  (tree) =>
    mergedTagTreesWithoutDuplicates(
      tree,
      pathAsTagTree(path, topLevelTagCollection),
    );

export const mergedListOfTagTrees = (listOfTags: ListOfTagTrees): TagTree =>
  listOfTags.reduce(mergedTagTreesWithoutDuplicates, { ...null });

export const shallowTagValue = (tag: string, tagTree: TagTree): TagValue =>
  tagTree[tag] || [];

export const shallowTagValueStrings = (
  tag: string,
  tagTree: TagTree,
): $ReadOnlyArray<string> => {
  const value = shallowTagValue(tag, tagTree);
  return Array.isArray(value) ? value : [];
};

export const exceptPath = (path: Path, tree: TagTree): TagTree => {
  const parent = path[0];
  if (typeof parent !== 'string') return tree;
  const target = path[1];

  const rest = path.slice(2);

  if (rest.length === 0) {
    // okay time to perform some action

    if (typeof target !== 'string') {
      // this is the special case where we're targeting a top-level key
      return withoutProperty(parent, tree);
    }

    const subject = tree[parent];

    if (Array.isArray(subject)) {
      const filtered = subject.filter((element) => element !== target);

      if (filtered.length === 0) return withoutProperty(parent, tree);

      return {
        ...tree,
        [parent]: filtered,
      };
    }

    const pruned = withoutProperty(target, subject);

    if (Object.keys(pruned).length === 0) return withoutProperty(parent, tree);

    return {
      ...tree,
      [parent]: pruned,
    };
  }

  // delve deeper into our tree
  return fromEntries(
    compact(
      Object.keys(tree).map((k) => {
        if (k === parent) {
          const subject = tree[k];

          // if subject is an array then our path is not going to match anyway
          // at this point, we already know we have a target we're gonna look
          // for within subject
          if (Array.isArray(subject)) return [k, subject];

          const result = exceptPath([target, ...rest], subject);

          if (Object.keys(result).length === 0) return null;

          return [k, result];
        }

        return [k, tree[k]];
      }),
    ),
  );
};

export const exceptPaths = (
  paths: $ReadOnlyArray<Path>,
  tree: TagTree,
): TagTree => paths.reduce((acc, path) => exceptPath(path, acc), tree);

export const paths = (tree: TagTree): $ReadOnlyArray<Path> => {
  // this gets all the simple paths to children that are not deeply nested
  const arrayPaths = (
    node: TagTree,
    prefix: $ReadOnlyArray<string>,
  ): $ReadOnlyArray<$ReadOnlyArray<string>> =>
    flatten(
      Object.keys(node)
        .map((k) => [node[k], k])
        .filter(([v]) => Array.isArray(v))
        .map(([v, k]): [$ReadOnlyArray<string>, string] => [(v: any), k])
        .map(([v, k]) =>
          v.map((value): $ReadOnlyArray<string> => [...prefix, k, value]),
        ),
    );

  // deepPaths is a structure that looks like this:
  //
  // [
  //  [string, string, string, TagTree],
  //  [string, string, TagTree],
  //  [string, string, string, TagTree],
  // ]
  //
  // where for each element array there will be exactly one TagTree, always in the
  // last position.
  //
  // only deeply nested (more than one level) paths are included.
  const deepPaths = treeRootOutPaths<TagTree>(
    // select all non-array children, which should be nested TagTrees
    (node): Array<TagTree> =>
      (Object.keys(node)
        .map((k) => node[k])
        .filter((child) => !Array.isArray(child)): any),
    tree,
  )
    // this is a special case where if no children are ever found then the root will
    // be returned as the only path. we don't want that in this case.
    .filter((nodePath: $ReadOnlyArray<TagTree>) => nodePath.length > 1)
    .map((nodePath: $ReadOnlyArray<TagTree>) =>
      nodePath.reduce(
        // modify each path so it looks like [string, string, TagTree]
        (
          acc: $ReadOnlyArray<string | TagTree>,
          node: TagTree,
          idx: number,
        ): $ReadOnlyArray<string | TagTree> => {
          const parent: TagTree = nodePath[idx - 1];

          if (!parent) return acc;

          const entryInParent: [string, TagValue] | void = Object.keys(parent)
            .map((k) => [k, parent[k]])
            .find(([, v]) => v === node);

          if (!entryInParent) return acc;

          const [keyInParent]: [string, TagValue] = entryInParent;
          // eslint-disable-next-line babel/no-unused-expressions
          (keyInParent: string);

          if (idx === nodePath.length - 1) {
            return [...acc, keyInParent, node];
          }

          return [...acc, keyInParent];
        },
        ([]: $ReadOnlyArray<string | TagTree>),
      ),
    );

  return [
    ...arrayPaths(tree, []),
    ...flatten(
      deepPaths
        .map((deepPath) => reverse(deepPath))
        .map((deepPath): $ReadOnlyArray<$ReadOnlyArray<string>> => {
          const head = deepPath[0];

          invariant(
            typeof head === 'object',
            'There must be a TagTree at the end of a deepPath',
          );

          // once we've removed the only possible TagTree, then the rest of these are
          // guaranteed to be strings
          const tail = ((deepPath.slice(1): any): $ReadOnlyArray<string>);

          // turn this into a list of lists of strings
          return arrayPaths((head: TagTree), reverse(tail));
        }),
    ),
  ];
};

export const pathsMatch =
  (first: Path): ((second: Path) => boolean) =>
  (second: Path): boolean =>
    equals(first, second);

export const pathStartsWith =
  (path: Path): ((start: Path) => boolean) =>
  (start: Path): boolean =>
    start.every((part, idx) => part === path[idx]);

export const onlyPaths = (only: $ReadOnlyArray<Path>, tree: TagTree): TagTree =>
  exceptPaths(
    paths(tree).filter((path) => !only.some(pathStartsWith(path))),
    tree,
  );

export const topLevelPathsCollapsed = (
  list: $ReadOnlyArray<Path>,
): $ReadOnlyArray<Path> => {
  const topLevelPaths = list.filter((path) => path.length === 1);
  return list.filter((path) => !topLevelPaths.some(pathStartsWith(path)));
};

export const hasPathStart =
  (path: Path): ((tagTree: TagTree) => boolean) =>
  (tagTree: TagTree): boolean =>
    paths(tagTree).some((treePath) => pathStartsWith(treePath)(path));

export const somePathStartsMatch =
  (pathList: $ReadOnlyArray<Path>): ((tagTree: TagTree) => boolean) =>
  (tagTree: TagTree): boolean =>
    pathList.some((path) => hasPathStart(path)(tagTree));

export const pathsGroupedByTag = (
  pathList: $ReadOnlyArray<Path>,
): $ReadOnlyArray<$ReadOnlyArray<Path>> => {
  const pathsByTag = groupBy(([tag]) => tag)(pathList);
  return Object.keys(pathsByTag).map((tag) => pathsByTag[tag]);
};

export const includesAPathFromEveryTag =
  (pathList: $ReadOnlyArray<Path>): ((tagTree: TagTree) => boolean) =>
  (tagTree: TagTree): boolean =>
    pathsGroupedByTag(pathList).every((group) =>
      somePathStartsMatch(group)(tagTree),
    );

export const tagTreeWithoutZeroNodes = (
  tagTree: TagTree,
  facetList?: $ReadOnlyArray<Facet>,
): TagTree => {
  if (!facetList) return tagTree;

  return exceptPaths(
    paths(tagTree).filter((path) => {
      const facet = facetByName(facetList, path[0]);
      if (!facet) return false;
      return termCountByPath(facet, path) === 0;
    }),
    tagTree,
  );
};
